/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import Util from 'bootstrap/js/src/util';

import { flyoutOptions } from '../options/flyout.options';

/**
 * Bootstrap-modal event namespace so the directive can detach only its own listeners.
 *
 * @protected
 * @readonly
 *
 * @type {string}
 */
const EVENT_NAMESPACE = '.amb-flyout';

/**
 * Resolved options for a single `amb-flyout` element.
 *
 * @typedef {object} FlyoutOptions
 *
 * @property {string}                 initialView   `view` name shown when the flyout opens / resets.
 * @property {string}                 viewSelector  CSS selector matching each sub-view element.
 * @property {string}                 viewAttribute Attribute on a sub-view holding its name.
 * @property {boolean}                moveToBody    Re-parent the flyout under `<body>` on init.
 * @property {boolean}                resetOnHidden Reset to `initialView` (empty history) on `hidden.bs.modal`.
 * @property {string|null}            triggerScope  Selector of an out-of-flyout container whose
 *                                                  `[data-view]` clicks open + switch this flyout.
 * @property {Record<string, string>} triggers      `data-view` value -> CSS selector of an on-page CTA to proxy-click.
 * @property {(detail: ViewChangeDetail, element: FlyoutElement) => void} [onViewChange]
 *   Optional hook fired BEFORE the slide swap (and on open) once the change is not cancelled - mirrors
 *   the `amb:modal-view-change` event. Use it to sync view-dependent chrome (e.g. a footer CTA).
 * @property {(detail: ViewChangeDetail, element: FlyoutElement) => void} [onViewChanged]
 *   Optional hook fired AFTER the slide transition completes (and on open) - mirrors the
 *   `amb:modal-view-changed` event.
 */

/**
 * Payload shared by the view-change events / hooks.
 *
 * @typedef {object} ViewChangeDetail
 *
 * @property {string|null} view         The view now being shown.
 * @property {string|null} previousView The view that was shown before (null on open).
 * @property {number}      history      Depth of the back-history stack after the change.
 * @property {string}      direction    `forward` / `back`.
 */

/**
 * Per-element state stored in {@link flyoutDirective.container}, keyed by `element.ambid`.
 *
 * @typedef {object} FlyoutState
 *
 * @property {HTMLElement}                element              The flyout root (directive host).
 * @property {FlyoutOptions}              options              Resolved directive options.
 * @property {string[]}                   viewHistory          Stack of previously-shown views for "back".
 * @property {number}                     transitionToken      Monotonic guard against stale transition callbacks.
 * @property {(event: MouseEvent) => void} clickListener       Delegated click listener (modal + trigger scope).
 * @property {() => void}                 showListener         Bootstrap `show.bs.modal` listener.
 * @property {() => void}                 hiddenListener         Bootstrap `hide.bs.modal` listener.
 * @property {HTMLElement[]}              triggerScopeElements Out-of-flyout containers bound to `clickListener`.
 */

/**
 * A flyout element augmented with the directive's public API.
 *
 * @typedef {HTMLElement & {
 *   setView: (view?: string|null) => void,
 *   resetViews: () => void,
 *   clearHistory: () => void
 * }} FlyoutElement
 */

/**
 * Flyout directive.
 *
 * Turns a Bootstrap modal into a flyout with sliding, history-aware nested sub-views. Attach
 * `amb-flyout` to the modal root; sub-views are `.modal-view[view="..."]` children.
 *
 * Written in the shared object-based style (a single directive instance handling many elements via
 * {@link flyoutDirective.container}). The public API (`setView` / `resetViews` / `clearHistory`) is also exposed
 * directly on each element so other entry-point bundles can drive the flyout via the live DOM node
 * (e.g. `document.querySelector('.configurator-modal').setView('change-model')`) without sharing
 * this module's instance.
 *
 * @example
 *
 * ```html
 * <div class="modal fade flyout configurator-modal" amb-flyout="configurator" data-view="main-menu">
 *   <div class="modal-view" view="main-menu">...</div>
 *   <div class="modal-view" view="change-model">...</div>
 * </div>
 * ```
 */
export const flyoutDirective = {
  /**
   * Options container.
   *
   * @type {import('../options/flyout.options').flyoutOptions}
   */
  options: flyoutOptions,

  /**
   * Per-element state, keyed by `element.ambid`.
   *
   * @protected
   *
   * @type {Map<string, FlyoutState>}
   */
  container: new Map(),

  /**
   * Initialize the flyout: re-parent under `<body>`, seed the initial view, bind listeners, and
   * expose the public API on the element.
   *
   * @param {FlyoutElement} element The flyout root.
   * @param {FlyoutOptions} options Resolved directive options.
   *
   * @returns {void}
   */
  init(element, options = {}) {
    if (this.container.has(element.ambid)) {
      return;
    }

    /**
     * @type {FlyoutState}
     */
    const state = {
      element,
      options,
      viewHistory: [],
      transitionToken: 0,
      initialView: element.dataset.view || options.initialView || null,
      clickListener: (event) => this.onClick(event, element),
      showListener: (event) => this.onShow(event, element),
      hiddenListener: (event) => this.onHidden(event, element),
      triggerScopeElements: []
    };

    this.container.set(element.ambid, state);

    if (options.moveToBody && element.parentElement !== document.body) {
      document.body.appendChild(element);
    }

    if (options.initialView && !element.dataset.view) {
      element.dataset.view = options.initialView;
    }

    this.resetViews(element);

    element.addEventListener('click', state.clickListener);

    if (options.triggerScope) {
      state.triggerScopeElements = Array.from(document.querySelectorAll(options.triggerScope));
      state.triggerScopeElements.forEach((scopeElement) => scopeElement.addEventListener('click', state.clickListener));
    }

    $(element)
      .on(`show.bs.modal${EVENT_NAMESPACE}`, state.showListener)
      .on(`hidden.bs.modal${EVENT_NAMESPACE}`, state.hiddenListener)
    ;

    // Expose the public API on the live DOM node so callers in other entry-point bundles (which get
    // a separate copy of this module with an empty `container`) can still drive the flyout.
    element.setView = (view = null) => this.setView(element, view);
    element.resetViews = () => this.resetViews(element);
    element.clearHistory = () => this.clearHistory(element);
  },

  /**
   * Tear down the flyout: detach every listener bound by {@link flyoutDirective.init} and drop state.
   *
   * @param {FlyoutElement} element The flyout root.
   *
   * @returns {void}
   */
  destroy(element) {
    const state = this.container.get(element.ambid);

    if (!state) {
      return;
    }

    element.removeEventListener('click', state.clickListener);
    state.triggerScopeElements.forEach((scopeElement) => scopeElement.removeEventListener('click', state.clickListener));
    $(element).off(EVENT_NAMESPACE);

    delete element.setView;
    delete element.resetViews;
    delete element.clearHistory;

    this.container.delete(element.ambid);
  },

  /**
   * Strip every position class from each sub-view and re-tag the one matching the flyout's current
   * `data-view` (or `options.initialView`) with `position-active`. Guarantees the next open starts
   * with the initial view visible and every other view `display: none`.
   *
   * @param {FlyoutElement} element The flyout root.
   *
   * @returns {void}
   */
  resetViews(element) {
    const state = this.container.get(element.ambid);

    if (!state) {
      return;
    }

    const { initialView, options } = state;

    element.querySelectorAll(options.viewSelector).forEach((view) => {
      view.classList.remove('position-active', 'position-left', 'position-right', 'no-transition');

      if (initialView && view.getAttribute(options.viewAttribute) === initialView) {
        view.classList.add('position-active');
      }
    });
  },

  /**
   * Clear the history of the flyout.
   *
   * @param {FlyoutElement} element The flyout root.
   *
   * @returns {void}
   */
  clearHistory(element) {
    const state = this.container.get(element.ambid);

    if (!state) {
      return;
    }

    state.viewHistory.length = 0;
    element.dataset.history = '0';
  },

  /**
   * Switch the flyout to `view`, or pop the previous view off the history when `view` is `null` /
   * `'back'`. Drives the slide transition by toggling `position-active` / `position-left` /
   * `position-right` classes on the involved sub-views and dispatches a pair of bubbling custom
   * events around the swap:
   *
   *   - `amb:modal-view-change`  - BEFORE the class swap. Cancelable; `preventDefault()` aborts it.
   *   - `amb:modal-view-changed` - AFTER the slide transition completes.
   *
   * Both events expose `{ view, previousView, history, direction }` on `event.detail`.
   *
   * @param {FlyoutElement}  element   The flyout root.
   * @param {string|null}    [view=null] Target view name, or `null` / `'back'` to go back.
   *
   * @returns {void}
   */
  setView(element, view = null) {
    const state = this.container.get(element.ambid);

    if (!state) {
      return;
    }

    const { options, viewHistory } = state;
    const isBack = !view || view === 'back';

    if (isBack) {
      if (viewHistory.length === 0) {
        return;
      }

      view = viewHistory.pop();
    }

    const previousView = element.dataset.view || null;

    if (view === previousView) {
      element.dataset.history = viewHistory.length;
      return;
    }

    const direction = isBack ? 'back' : 'forward';
    const detail = { view, previousView, history: viewHistory.length, direction };

    const beforeEvent = new CustomEvent('amb:modal-view-change', { bubbles: true, cancelable: true, detail });

    if (!element.dispatchEvent(beforeEvent)) {
      // Restore the popped history entry so a cancelled "back" doesn't lose its breadcrumb.
      if (isBack) {
        viewHistory.push(view);
      }

      return;
    } else if (typeof options.onViewChange === 'function') {
      options.onViewChange(detail, element);
    }

    const previousViewEl = previousView ? element.querySelector(`${options.viewSelector}[${options.viewAttribute}="${previousView}"]`) : null;
    const viewEl = element.querySelector(`${options.viewSelector}[${options.viewAttribute}="${view}"]`);

    // Snap the incoming view to its starting edge (right for forward, left for back) before
    // animating to centre - guards against forward-pushing to a view that was last parked on the
    // opposite side from a previous transition.
    if (viewEl) {
      this.snapViewPosition(viewEl, isBack ? 'left' : 'right');
    }

    if (previousViewEl) {
      previousViewEl.classList.remove('position-active');
      previousViewEl.classList.add(isBack ? 'position-right' : 'position-left');
    }

    if (viewEl) {
      viewEl.classList.remove('position-left', 'position-right');
      viewEl.classList.add('position-active');
    }

    element.dataset.view = view;
    element.dataset.previousView = previousView || '';
    element.dataset.history = viewHistory.length;

    const token = ++state.transitionToken;
    const transitionEl = previousViewEl || viewEl;
    const duration = Math.max(
      Util.getTransitionDurationFromElement(viewEl),
      Util.getTransitionDurationFromElement(previousViewEl)
    );

    const onTransitionEnd = () => {
      if (state.transitionToken !== token) {
        return;
      }

      if (previousViewEl) {
        previousViewEl.classList.remove('position-active', 'position-left', 'position-right');
      }

      element.dispatchEvent(new CustomEvent('amb:modal-view-changed', { bubbles: true, cancelable: false, detail }));

      if (typeof options.onViewChanged === 'function') {
        options.onViewChanged(detail, element);
      }
    };

    if (transitionEl) {
      $(transitionEl).one(Util.TRANSITION_END, onTransitionEnd).emulateTransitionEnd(duration);
    } else {
      onTransitionEnd();
    }
  },

  /**
   * Snap a sub-view to its starting off-screen `position` (`'left'` or `'right'`) without triggering
   * a transition, so the subsequent slide-in always begins from the correct edge.
   *
   * @protected
   *
   * @param {HTMLElement}       viewEl   The sub-view element.
   * @param {'left' | 'right'}  position The edge to snap to.
   *
   * @returns {void}
   */
  snapViewPosition(viewEl, position) {
    viewEl.classList.add('no-transition');
    viewEl.classList.remove('position-active', 'position-left', 'position-right');
    viewEl.classList.add(`position-${position}`);

    // Force a synchronous layout so the snapped state is committed as the transition's "from"
    // computed style before `no-transition` is removed.
    viewEl.getBoundingClientRect();
    viewEl.classList.remove('no-transition');
  },

  /**
   * Delegated click handler bound to both the flyout and any `triggerScope` containers.
   *
   * Handles only `[data-view]` elements:
   *   - `triggers[view]` defined -> proxy-click the mapped on-page CTA (hiding the flyout first when
   *     the click originated inside it).
   *   - otherwise -> swap to that view via {@link flyoutDirective.setView}, pushing the current view
   *     onto history, and open the flyout when the click came from outside it.
   *
   * `[data-action]` and attribute-less clicks are ignored - those remain the navigation's concern.
   *
   * @protected
   *
   * @param {MouseEvent}    event   The click event.
   * @param {FlyoutElement} element The flyout root this listener belongs to.
   *
   * @returns {void}
   */
  onClick(event, element) {
    const state = this.container.get(element.ambid);

    if (!state) {
      return;
    }

    const { options } = state;
    const button = event.target.closest('a, button');

    if (!button || !button.dataset.view) {
      return;
    }

    const inModal = element.contains(button);
    const view = button.dataset.view;

    if (options.triggers[view]) {
      if (typeof options.triggers[view] === 'function') {
        return options.triggers[view](event, element);
      } else if (inModal) {
        $(element)
          .one('hidden.bs.modal', () => $(options.triggers[view]).trigger('click'))
          .modal('hide')
        ;
      } else {
        $(options.triggers[view]).trigger('click');
      }

      return;
    }

    if (inModal && view !== 'back') {
      state.viewHistory.push(element.dataset.view);
    }

    // Swap the view before showing so Bootstrap fires `show.bs.modal` with the correct `data-view`
    // already in place, otherwise the flyout flashes the default view for one frame.
    this.setView(element, view);

    // A click that originates OUTSIDE the flyout needs to actually open it; clicks from inside are
    // already inside an open flyout and just swap views.
    if (!inModal) {
      $(element).modal('show');
    }
  },

  /**
   * Bootstrap `show.bs.modal` hook. Placeholder for parity with the legacy implementation.
   *
   * @protected
   *
   * @param {JQuery.EventBase}    event   The show event.
   * @param {FlyoutElement} element The flyout root.
   *
   * @returns {void}
   */
  onShow(event, element) {
    const relatedTarget = event.relatedTarget;

    if (relatedTarget && relatedTarget.dataset.view) {
      this.setView(element, relatedTarget.dataset.view);
    }
  },

  /**
   * Bootstrap `hidden.bs.modal` hook. Resets the flyout back to its initial view with an empty
   * history so the next open starts fresh.
   *
   * @protected
   *
   * @param {FlyoutElement} element The flyout root.
   *
   * @returns {void}
   */
  onHidden(event, element) {
    const state = this.container.get(element.ambid);

    if (!state) {
      return;
    }

    const { initialView, options, viewHistory } = state;

    if (!options.resetOnHidden) {
      return;
    }

    viewHistory.length = 0;
    element.dataset.view = initialView;
    element.dataset.history = '0';

    this.resetViews(element);
  }
};
